import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RecorderHandle } from '../hooks/useAudioWorkletRecorder';
import WaveformVisualizer from './WaveformVisualizer';

const HARD_CAP_MS = 60_000;
const SOFT_WARN_MS = 45_000;
const MIN_DURATION_MS = 500;
/** Auto-finish + send after N ms of silence while recording. Long enough that
 *  the speaker can pause mid-sentence without being cut off, short enough that
 *  the learner doesn't have to manually tap stop in the common case. */
const AUTO_STOP_SILENCE_MS = 2500;

type ButtonState = 'idle' | 'recording' | 'sending' | 'error';

interface Props {
    /** Called with the captured PCM frames + sampleRate when the user finishes. */
    onComplete: (frames: Float32Array[], sampleRate: number) => Promise<void> | void;
    /** Called when the upload-or-anything fails so the parent can toast. */
    onError?: (msg: string) => void;
    /** External lock — e.g. while an upload is in flight. */
    disabled?: boolean;
    /** Recorder handle is owned by the parent so it can prewarm permission. */
    recorder: RecorderHandle;
}

/**
 * Tap-to-toggle voice capture.
 *
 *   idle      ─tap▶ recording
 *   recording ─tap | Enter | 2.5s-silence▶ sending ▶ idle
 *   recording ─Esc / cancel▶ idle (no send)
 *
 * Mic permission is requested OUTSIDE this component (parent calls
 * `recorder.prewarm()` on mount). That fixes the mobile-Chrome bug where
 * a `getUserMedia()` prompt appearing mid-press scrambled the pointer
 * state and produced a flood of phantom 1-second uploads.
 *
 * Visual states are deliberately strong (red pulse + waveform + ticker)
 * so the user always knows whether the mic is hot — the previous
 * press-and-hold flow left users guessing.
 */
const PTTButton: React.FC<Props> = ({ onComplete, onError, disabled, recorder }) => {
    const { t } = useTranslation('messaging');
    const [buttonState, setButtonState] = useState<ButtonState>('idle');
    const [recordingMs, setRecordingMs] = useState(0);
    const [tooShortHint, setTooShortHint] = useState(false);
    const recordStartRef = useRef<number>(0);
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const autoStopArmedRef = useRef<boolean>(false);

    const isSupported = recorder.isSupported;
    const permission = recorder.permission;

    const stopTicker = useCallback(() => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);

    const finishAndSend = useCallback(
        async () => {
            stopTicker();
            const elapsed = Date.now() - recordStartRef.current;
            const captured = recorder.stop();

            if (elapsed < MIN_DURATION_MS || captured.frames.length === 0) {
                setTooShortHint(true);
                setButtonState('idle');
                setRecordingMs(0);
                setTimeout(() => setTooShortHint(false), 1800);
                return;
            }

            setButtonState('sending');
            try {
                await onComplete(captured.frames, captured.sampleRate);
            } catch (err) {
                onError?.((err as Error).message);
                setButtonState('error');
                setTimeout(() => setButtonState('idle'), 1500);
                setRecordingMs(0);
                return;
            }
            setButtonState('idle');
            setRecordingMs(0);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [recorder, onComplete, onError, stopTicker],
    );

    const cancelRecording = useCallback(() => {
        stopTicker();
        recorder.cancel();
        autoStopArmedRef.current = false;
        setButtonState('idle');
        setRecordingMs(0);
    }, [recorder, stopTicker]);

    const beginRecording = useCallback(async () => {
        if (disabled || !isSupported) return;
        if (permission === 'denied') {
            onError?.('permission_denied');
            return;
        }

        autoStopArmedRef.current = false;
        recordStartRef.current = Date.now();
        setRecordingMs(0);
        setButtonState('recording');

        try {
            await recorder.start();
        } catch (err) {
            setButtonState('error');
            onError?.((err as Error).message);
            setTimeout(() => setButtonState('idle'), 1500);
            return;
        }

        tickRef.current = setInterval(() => {
            const elapsed = Date.now() - recordStartRef.current;
            setRecordingMs(elapsed);
            if (elapsed >= HARD_CAP_MS) finishAndSend();
        }, 100);
    }, [disabled, isSupported, permission, recorder, onError, finishAndSend]);

    const handleToggle = useCallback(() => {
        if (buttonState === 'recording') {
            finishAndSend();
        } else if (buttonState === 'idle') {
            beginRecording();
        }
        // 'sending' / 'error' → ignore taps until state returns to idle.
    }, [buttonState, finishAndSend, beginRecording]);

    // Auto-stop on sustained silence: arm 1.2s after recording starts (give
    // the user time to draw breath before the first word), then if `peak`
    // stays under threshold for AUTO_STOP_SILENCE_MS, finish and send.
    const lastVoiceMsRef = useRef<number>(0);
    useEffect(() => {
        if (buttonState !== 'recording') {
            autoStopArmedRef.current = false;
            return;
        }
        lastVoiceMsRef.current = Date.now();
        const armTimer = setTimeout(() => {
            autoStopArmedRef.current = true;
        }, 1200);
        return () => clearTimeout(armTimer);
    }, [buttonState]);

    useEffect(() => {
        if (buttonState !== 'recording') return;
        const SILENCE_THRESHOLD = 0.02;
        const now = Date.now();
        if (recorder.peak > SILENCE_THRESHOLD) {
            lastVoiceMsRef.current = now;
            return;
        }
        if (
            autoStopArmedRef.current &&
            now - lastVoiceMsRef.current > AUTO_STOP_SILENCE_MS
        ) {
            finishAndSend();
        }
    }, [recorder.peak, buttonState, finishAndSend]);

    // Keyboard: Space toggles, Esc cancels — same UX on desktop and mobile.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement | null)?.tagName ?? '';
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.code === 'Space' && !e.repeat) {
                e.preventDefault();
                handleToggle();
            } else if (e.code === 'Escape' && buttonState === 'recording') {
                e.preventDefault();
                cancelRecording();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [handleToggle, cancelRecording, buttonState]);

    useEffect(() => stopTicker, [stopTicker]);

    if (!isSupported) {
        return (
            <div
                className="px-3 py-2 m-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg"
                data-testid="ptt-unsupported"
            >
                {t('voice.unsupported', 'Voice mode requires a modern browser with microphone support.')}
            </div>
        );
    }

    if (permission === 'denied') {
        return (
            <div
                className="px-3 py-2 m-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg"
                data-testid="ptt-permission-denied"
            >
                {t('voice.permissionDenied', 'Microphone access was blocked. Please enable it in your browser settings to use voice chat.')}
            </div>
        );
    }

    const showWarn = recordingMs >= SOFT_WARN_MS;
    const seconds = (recordingMs / 1000).toFixed(1);

    const buttonLabel =
        buttonState === 'recording'
            ? t('voice.tapToStop', 'Tap to stop and send')
            : buttonState === 'sending'
              ? t('voice.sending', 'Sending...')
              : permission === 'pending'
                ? t('voice.allowMic', 'Allow microphone access')
                : t('voice.tapToRecord', 'Tap to record');

    return (
        <div
            className="flex flex-col items-center w-full px-4 py-4"
            data-ptt-state={buttonState}
            data-testid="ptt-root"
        >
            {buttonState === 'recording' && (
                <div
                    className="mb-3 w-full max-w-sm rounded-2xl px-4 py-3 bg-primary-500"
                    data-testid="ptt-waveform-tray"
                >
                    <WaveformVisualizer peak={recorder.peak} barCount={28} />
                    <div className="mt-2 flex items-center justify-between text-white/90 text-xs font-mono">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                            {t('voice.recordingShort', 'REC')}
                        </span>
                        <span className={showWarn ? 'text-amber-200 font-bold' : ''}>{seconds}s</span>
                    </div>
                </div>
            )}

            {tooShortHint && (
                <div
                    className="mb-2 px-3 py-1.5 text-xs text-gray-500 bg-gray-100 rounded-full"
                    data-testid="ptt-too-short"
                >
                    {t('voice.tooShort', 'Recording too short — try again')}
                </div>
            )}

            <div className="flex items-center gap-3">
                {buttonState === 'recording' && (
                    <button
                        type="button"
                        onClick={cancelRecording}
                        aria-label={t('voice.cancel', 'Cancel')}
                        className="
                            w-11 h-11 rounded-full bg-gray-100 hover:bg-gray-200
                            flex items-center justify-center text-gray-600
                            focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300
                        "
                        data-testid="ptt-cancel"
                    >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                            <path d="M6.225 4.811a1 1 0 0 1 1.414 0L12 9.172l4.361-4.361a1 1 0 1 1 1.414 1.414L13.414 10.586l4.361 4.361a1 1 0 1 1-1.414 1.414L12 12l-4.361 4.361a1 1 0 0 1-1.414-1.414l4.361-4.361-4.361-4.361a1 1 0 0 1 0-1.414z" />
                        </svg>
                    </button>
                )}

                <button
                    type="button"
                    aria-label={buttonLabel}
                    title={buttonLabel}
                    onClick={handleToggle}
                    disabled={disabled || buttonState === 'sending' || permission === 'pending'}
                    className={`
                        w-16 h-16 rounded-full flex items-center justify-center
                        select-none touch-none transition-transform
                        focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-300
                        ${buttonState === 'recording'
                            ? 'bg-red-500 hover:bg-red-600 shadow-lg animate-[pulse_1.6s_ease-in-out_infinite]'
                            : 'bg-primary-500 hover:bg-primary-600 active:scale-95'
                        }
                        ${buttonState === 'sending' ? 'opacity-70 cursor-wait' : ''}
                        disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                    data-testid="ptt-button"
                >
                    {buttonState === 'sending' ? (
                        <div className="w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    ) : buttonState === 'recording' ? (
                        // Stop square
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="white" aria-hidden="true">
                            <rect x="6" y="6" width="12" height="12" rx="2" />
                        </svg>
                    ) : (
                        // Microphone
                        <svg viewBox="0 0 24 24" width="28" height="28" fill="white" aria-hidden="true">
                            <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z" />
                            <path d="M19 11a1 1 0 0 0-2 0 5 5 0 0 1-10 0 1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V21a1 1 0 0 0 2 0v-3.07A7 7 0 0 0 19 11z" />
                        </svg>
                    )}
                </button>
            </div>

            <p className="mt-2 text-xs text-gray-500" aria-live="polite">
                {buttonState === 'recording'
                    ? t('voice.tapAgainOrEsc', 'Tap to send · Esc to cancel')
                    : buttonLabel}
            </p>
        </div>
    );
};

export default PTTButton;
